"use client";

import { useState, useCallback, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHandPointer,
  faCog,
  faPaintBrush,
  faTrash,
  faSun,
  faMoon,
} from "@fortawesome/free-solid-svg-icons";
import { MobileDragHandle } from "./MobileDragHandle";
import MobileSettingsDrawer from "./MobileSettingsDrawer";
import { compactLayoutVertically } from "../../hooks/useDragAndDrop";

/**
 * MobileWidgetEditor hook handles mobile-specific widget editing functionality
 * Including drag handles, widget selection, and mobile control bars
 * Now uses the main drag and drop system for consistency
 */
export function useMobileWidgetEditor({
  selectedWidgetId,
  onWidgetSelect,
  onWidgetDeselect,
  widgets,
  onWidgetUpdate,
  onWidgetDelete,
  onOpenSettings,
  isMobileDevice,
  previewMode,
  containerWidth,
  performMasonryLayout,
  updateContainerHeight,
  isLightBackground = false,
  // Main drag system props
  dragSystem,
}) {
  // State for size selector menu
  const [showSizeMenu, setShowSizeMenu] = useState(false);

  // State for mobile settings drawer
  const [mobileDrawer, setMobileDrawer] = useState({
    isOpen: false,
    settingsType: null, // 'config' or 'layout'
    widget: null,
  });

  // Close size menu when widget is deselected
  useEffect(() => {
    if (!selectedWidgetId) {
      setShowSizeMenu(false);
    }
  }, [selectedWidgetId]);

  // Handle mobile settings drawer
  const handleMobileSettingsOpen = useCallback(
    (widgetId, settingsType) => {
      const widget = widgets.find((w) => w.id === widgetId);
      if (!widget) return;

      setMobileDrawer({
        isOpen: true,
        settingsType,
        widget,
      });
    },
    [widgets]
  );

  const handleMobileDrawerClose = useCallback(() => {
    setMobileDrawer({ isOpen: false, settingsType: null, widget: null });
  }, []);

  const handleMobileConfigChange = useCallback(
    (config) => {
      if (mobileDrawer.widget) {
        onWidgetUpdate(mobileDrawer.widget.id, { config });
      }
    },
    [mobileDrawer.widget, onWidgetUpdate]
  );

  const handleMobileLayoutChange = useCallback(
    (layout, options = {}) => {
      if (mobileDrawer.widget) {
        onWidgetUpdate(mobileDrawer.widget.id, { layout }, options);

        // Apply vertical compaction after layout changes
        if (!options.isRealTimeUpdate) {
          const currentPreviewMode = previewMode || "mobile";
          const totalColumns =
            currentPreviewMode === "mobile"
              ? 4
              : currentPreviewMode === "tablet"
                ? 8
                : 12;

          const compactedWidgets = compactLayoutVertically(
            widgets.map((w) =>
              w.id === mobileDrawer.widget.id ? { ...w, layout } : w
            ),
            currentPreviewMode,
            totalColumns
          );

          // Update all widgets with compacted layout
          onWidgetUpdate(null, {
            type: "UPDATE_WIDGET",
            widgets: compactedWidgets,
            triggerAutosave: true,
          });

          // Force layout recalculation
          setTimeout(() => {
            performMasonryLayout(containerWidth, compactedWidgets);
            if (updateContainerHeight) {
              updateContainerHeight();
            }
          }, 100);
        }
      }
    },
    [
      mobileDrawer.widget,
      widgets,
      previewMode,
      onWidgetUpdate,
      performMasonryLayout,
      containerWidth,
      updateContainerHeight,
    ]
  );

  /**
   * Handle widget resize with comprehensive collision detection and repositioning
   * Following the same strategy as EditableWidget for consistency
   */
  const handleWidgetResize = useCallback(
    (widgetId, newColumns, newColumnHeight) => {
      const widget = widgets.find((w) => w.id === widgetId);
      if (!widget) return;

      const currentPreviewMode = previewMode || "mobile";
      const totalColumns =
        currentPreviewMode === "mobile"
          ? 4
          : currentPreviewMode === "tablet"
            ? 8
            : 12;

      console.log(
        `[Mobile Resize Debug] Starting resize for widget ${widgetId}:`,
        {
          newColumns,
          newColumnHeight,
          currentLayout: widget.layout[currentPreviewMode],
          currentPreviewMode,
          allWidgetsCount: widgets.length,
        }
      );

      // Get current layout values
      const currentStartCol =
        widget.layout[currentPreviewMode]?.startColumn || 0;
      const currentPosition = widget.layout[currentPreviewMode]?.position || 0;

      // Calculate new startColumn, adjusting if resizing would push widget off the right edge
      let newStartColumn = currentStartCol;

      // If widget would exceed right edge after resize, adjust startColumn
      if (currentStartCol + newColumns > totalColumns) {
        newStartColumn = Math.max(0, totalColumns - newColumns);
      }

      const newDimensions = {
        columns: newColumns,
        columnHeight: newColumnHeight,
        startColumn: newStartColumn,
        position: currentPosition,
      };

      // Create updated layout for current viewport only
      const updatedLayout = {
        ...widget.layout,
        [currentPreviewMode]: {
          ...widget.layout[currentPreviewMode],
          ...newDimensions,
        },
      };

      console.log(
        `[Mobile Resize Debug] Updated widget layout for ${currentPreviewMode}:`,
        updatedLayout[currentPreviewMode]
      );

      // Calculate layout changes with comprehensive collision detection
      const calculateLayoutChanges = () => {
        // Create a working copy of all widgets
        const workingWidgets = widgets.map((w) => ({
          ...w,
          layout: JSON.parse(JSON.stringify(w.layout)), // Deep clone layout
        }));

        console.log(
          "[Mobile Resize Debug] Working widgets created:",
          workingWidgets.length
        );

        // Update the resizing widget in the working copy
        const resizingWidget = workingWidgets.find((w) => w.id === widgetId);
        if (!resizingWidget) {
          console.error(
            "[Mobile Resize Debug] Could not find resizing widget!"
          );
          return {};
        }

        // Apply the updated layout to the resizing widget
        resizingWidget.layout = updatedLayout;

        // Build widget map for collision detection
        const widgetMap = new Map();
        const lockedWidgets = [];

        workingWidgets.forEach((item) => {
          const itemLayout = item.layout[currentPreviewMode];
          const widgetInfo = {
            id: item.id,
            position: itemLayout?.position || 0,
            height: itemLayout?.columnHeight || 4,
            startColumn: itemLayout?.startColumn || 0,
            columns: itemLayout?.columns || 4,
            locked: item.layout?.settings?.locked || false,
            item: item,
          };

          widgetMap.set(item.id, widgetInfo);

          if (widgetInfo.locked) {
            lockedWidgets.push(widgetInfo);
          }
        });

        console.log("[Mobile Resize Debug] Widget map built:", {
          totalWidgets: widgetMap.size,
          lockedWidgets: lockedWidgets.length,
        });

        // Get the resizing widget from map
        const resizingInfo = widgetMap.get(widgetId);
        let adjustedPosition = resizingInfo.position;

        // Check for locked widget conflicts
        for (const lockedWidget of lockedWidgets) {
          const hasVerticalOverlap = !(
            adjustedPosition + newColumnHeight <= lockedWidget.position ||
            adjustedPosition >= lockedWidget.position + lockedWidget.height
          );

          const hasHorizontalOverlap = !(
            newStartColumn + newColumns <= lockedWidget.startColumn ||
            newStartColumn >= lockedWidget.startColumn + lockedWidget.columns
          );

          if (hasVerticalOverlap && hasHorizontalOverlap) {
            adjustedPosition = Math.max(
              adjustedPosition,
              lockedWidget.position + lockedWidget.height
            );
            console.log(
              "[Mobile Resize Debug] Adjusted position due to locked widget:",
              adjustedPosition
            );
          }
        }

        // Update resizing widget position if adjusted
        if (adjustedPosition !== resizingInfo.position) {
          resizingInfo.position = adjustedPosition;
          resizingWidget.layout[currentPreviewMode].position = adjustedPosition;
        }

        // Track all position changes
        const positionChanges = new Map();

        // Add the resizing widget's changes for current viewport only
        const currentLayout = updatedLayout[currentPreviewMode];
        positionChanges.set(widgetId, {
          position: adjustedPosition,
          startColumn: currentLayout.startColumn,
          columns: currentLayout.columns,
          columnHeight: currentLayout.columnHeight,
        });

        // Process layout conflicts
        const processQueue = [];

        // Find initial conflicts
        widgetMap.forEach((widget) => {
          if (widget.locked || widget.id === widgetId) return;

          const hasVerticalOverlap = !(
            adjustedPosition + newColumnHeight <= widget.position ||
            adjustedPosition >= widget.position + widget.height
          );

          const hasHorizontalOverlap = !(
            newStartColumn + newColumns <= widget.startColumn ||
            newStartColumn >= widget.startColumn + widget.columns
          );

          if (hasVerticalOverlap && hasHorizontalOverlap) {
            const pushAmount =
              adjustedPosition + newColumnHeight - widget.position;
            if (pushAmount > 0) {
              console.log(
                `[Mobile Resize Debug] Widget ${widget.id} needs to be pushed by ${pushAmount}`
              );
              processQueue.push({
                widget: widget,
                newPosition: widget.position + pushAmount,
              });
            }
          }
        });

        console.log(
          "[Mobile Resize Debug] Initial conflicts found:",
          processQueue.length
        );

        // Process the queue
        const movedWidgets = new Map();

        while (processQueue.length > 0) {
          const { widget, newPosition } = processQueue.shift();

          if (
            movedWidgets.has(widget.id) &&
            movedWidgets.get(widget.id) >= newPosition
          ) {
            continue;
          }

          let finalPosition = newPosition;

          // Check locked widget conflicts
          for (const lockedWidget of lockedWidgets) {
            const wouldOverlapVertically = !(
              finalPosition + widget.height <= lockedWidget.position ||
              finalPosition >= lockedWidget.position + lockedWidget.height
            );

            const wouldOverlapHorizontally = !(
              widget.startColumn + widget.columns <= lockedWidget.startColumn ||
              widget.startColumn >=
                lockedWidget.startColumn + lockedWidget.columns
            );

            if (wouldOverlapVertically && wouldOverlapHorizontally) {
              finalPosition = Math.max(
                finalPosition,
                lockedWidget.position + lockedWidget.height
              );
            }
          }

          // Check conflicts with previously moved widgets
          movedWidgets.forEach((movedPosition, movedWidgetId) => {
            const movedWidgetInfo = widgetMap.get(movedWidgetId);
            if (!movedWidgetInfo) return;

            const wouldOverlapVertically = !(
              finalPosition + widget.height <= movedPosition ||
              finalPosition >= movedPosition + movedWidgetInfo.height
            );

            const wouldOverlapHorizontally = !(
              widget.startColumn + widget.columns <=
                movedWidgetInfo.startColumn ||
              widget.startColumn >=
                movedWidgetInfo.startColumn + movedWidgetInfo.columns
            );

            if (wouldOverlapVertically && wouldOverlapHorizontally) {
              finalPosition = Math.max(
                finalPosition,
                movedPosition + movedWidgetInfo.height
              );
            }
          });

          movedWidgets.set(widget.id, finalPosition);

          // Update the widget position in the map immediately
          widget.position = finalPosition;
          widgetMap.set(widget.id, widget);

          // Track this change
          positionChanges.set(widget.id, {
            position: finalPosition,
            startColumn: widget.startColumn,
            columns: widget.columns,
            columnHeight: widget.height,
          });

          console.log(
            `[Mobile Resize Debug] Widget ${widget.id} moved to position ${finalPosition}`
          );

          // Check for new layout conflicts using updated positions
          widgetMap.forEach((otherWidget) => {
            if (
              otherWidget.id === widget.id ||
              otherWidget.locked ||
              movedWidgets.has(otherWidget.id) ||
              otherWidget.id === widgetId
            ) {
              return;
            }

            const currentOtherPosition = movedWidgets.has(otherWidget.id)
              ? movedWidgets.get(otherWidget.id)
              : otherWidget.position;

            const hasVerticalOverlap = !(
              finalPosition + widget.height <= currentOtherPosition ||
              finalPosition >= currentOtherPosition + otherWidget.height
            );

            const hasHorizontalOverlap = !(
              widget.startColumn + widget.columns <= otherWidget.startColumn ||
              widget.startColumn >=
                otherWidget.startColumn + otherWidget.columns
            );

            if (hasVerticalOverlap && hasHorizontalOverlap) {
              const otherPushAmount =
                finalPosition + widget.height - currentOtherPosition;
              if (otherPushAmount > 0) {
                const newOtherPosition = currentOtherPosition + otherPushAmount;

                // Only add to queue if not already processed with equal or greater position
                const existingQueueItem = processQueue.find(
                  (q) => q.widget.id === otherWidget.id
                );
                if (existingQueueItem) {
                  if (newOtherPosition > existingQueueItem.newPosition) {
                    existingQueueItem.newPosition = newOtherPosition;
                  }
                } else if (
                  !movedWidgets.has(otherWidget.id) ||
                  movedWidgets.get(otherWidget.id) < newOtherPosition
                ) {
                  processQueue.push({
                    widget: otherWidget,
                    newPosition: newOtherPosition,
                  });
                }
              }
            }
          });
        }

        console.log(
          "[Mobile Resize Debug] Total position changes:",
          positionChanges.size
        );

        return positionChanges;
      };

      // Calculate all position changes
      const positionChanges = calculateLayoutChanges();

      // Create batch update object
      const widgetUpdates = {};

      positionChanges.forEach((changes, widgetId) => {
        const targetWidget = widgets.find((w) => w.id === widgetId);
        if (targetWidget) {
          widgetUpdates[widgetId] = {
            layout: {
              ...targetWidget.layout,
              [currentPreviewMode]: {
                ...targetWidget.layout[currentPreviewMode],
                ...changes,
              },
            },
          };
          console.log(
            `[Mobile Resize Debug] Update prepared for widget ${widgetId}:`,
            changes
          );
        }
      });

      // Apply all updates at once
      if (Object.keys(widgetUpdates).length > 0) {
        console.log(
          "[Mobile Resize Debug] Applying layout updates to",
          Object.keys(widgetUpdates).length,
          "widgets"
        );

        // Convert to the format expected by onWidgetUpdate
        const updatedWidgets = widgets.map((widget) => {
          if (widgetUpdates[widget.id]) {
            return {
              ...widget,
              layout: {
                ...widget.layout,
                ...widgetUpdates[widget.id].layout,
              },
            };
          }
          return widget;
        });

        // Update all widgets at once
        onWidgetUpdate(null, {
          type: "UPDATE_WIDGET",
          widgets: updatedWidgets,
          triggerAutosave: true,
        });

        // Force layout recalculation after DOM updates
        requestAnimationFrame(() => {
          console.log("[Mobile Resize Debug] Triggering layout recalculation");
          performMasonryLayout(containerWidth);
          if (updateContainerHeight) {
            updateContainerHeight();
          }
        });

        // Apply compaction after a delay
        setTimeout(() => {
          console.log("[Mobile Resize Debug] Starting compaction phase");

          const compactedLayout = compactLayoutVertically(
            updatedWidgets,
            currentPreviewMode,
            totalColumns,
            [widgetId] // Exclude the resized widget from compaction
          );

          // Find changes from compaction
          const compactedUpdates = {};
          let hasCompactionChanges = false;

          compactedLayout.forEach((widget) => {
            if (widget.id === widgetId) return; // Skip the resized widget

            const currentWidget = updatedWidgets.find(
              (w) => w.id === widget.id
            );
            if (!currentWidget) return;

            if (
              widget.layout[currentPreviewMode]?.position !==
              currentWidget.layout[currentPreviewMode]?.position
            ) {
              hasCompactionChanges = true;
              compactedUpdates[widget.id] = {
                layout: { ...widget.layout },
              };
            }
          });

          if (hasCompactionChanges) {
            console.log("[Mobile Resize Debug] Applying compaction changes");

            // Apply compaction updates
            onWidgetUpdate(null, {
              type: "UPDATE_WIDGET",
              widgets: compactedLayout,
              triggerAutosave: true,
            });

            // Force layout recalculation after compaction
            requestAnimationFrame(() => {
              performMasonryLayout(containerWidth);
              if (updateContainerHeight) {
                updateContainerHeight();
              }
            });
          }
        }, 200);
      }

      setShowSizeMenu(false); // Close size menu after selection
    },
    [
      widgets,
      previewMode,
      onWidgetUpdate,
      performMasonryLayout,
      containerWidth,
      updateContainerHeight,
    ]
  );

  /**
   * Handle tap outside widgets to close edit menu
   */
  const handleContainerTap = useCallback(
    (event) => {
      // Don't handle if tap is on mobile edit bar, drag handle, or their children
      if (
        event.target.closest(".mobile-edit-bar") ||
        event.target.closest(".mobile-drag-handle")
      ) {
        return;
      }

      // Don't handle if currently dragging
      if (dragSystem?.isDragging) {
        return;
      }

      // Close size menu if it's open and tap is outside
      if (showSizeMenu) {
        setShowSizeMenu(false);
        return;
      }

      // Close mobile drawer if open and tap is outside edit bar
      if (mobileDrawer.isOpen) {
        setMobileDrawer({ isOpen: false, settingsType: null, widget: null });
        return;
      }

      // Only deselect if we have a selected widget and the tap wasn't on a widget, edit bar, or drag handle
      if (
        selectedWidgetId &&
        !event.target.closest("[data-widget-id]") &&
        !event.target.closest(".mobile-edit-bar") &&
        !event.target.closest(".mobile-drag-handle")
      ) {
        onWidgetDeselect();
      }
    },
    [
      selectedWidgetId,
      onWidgetDeselect,
      showSizeMenu,
      mobileDrawer.isOpen,
      dragSystem?.isDragging,
    ]
  );

  /**
   * Handle widget tap for selection
   */
  const handleWidgetTap = useCallback(
    (widgetId, e) => {
      e.stopPropagation();

      // Don't handle selection during drag
      if (dragSystem?.isDragging) {
        return;
      }

      if (selectedWidgetId === widgetId) {
        // Already selected - don't deselect on tap
        return;
      }

      onWidgetSelect(widgetId);
    },
    [selectedWidgetId, onWidgetSelect, dragSystem?.isDragging]
  );

  /**
   * Get current size icon for display
   */
  const getCurrentSizeIcon = useCallback(
    (widget) => {
      if (!widget) return "w-3 h-3";

      const layout = widget.layout[previewMode || "mobile"];
      const { columns, columnHeight } = layout;

      if (columns === 2 && columnHeight === 2) return "w-2.5 h-2.5"; // Mini
      if (columns === 4 && columnHeight === 2) return "w-4 h-2.5"; // Wide
      if (columns === 2 && columnHeight === 4) return "w-2.5 h-4"; // Tall
      if (columns === 4 && columnHeight === 4) return "w-3.5 h-3.5"; // Standard
      if (columns === 4 && columnHeight === 1) return "w-4 h-1.5"; // Skinny

      return "w-3 h-3"; // Default
    },
    [previewMode]
  );

  /**
   * Get available size options for widget
   */
  const getAvailableSizes = useCallback(
    (widget) => {
      if (!widget) return [];

      const sizes = [];
      const currentLayout = widget.layout[previewMode || "mobile"];

      if (widget.widget_type.mini) {
        sizes.push({
          columns: 2,
          columnHeight: 2,
          icon: "w-2.5 h-2.5",
          dimensions: "2×2",
          isActive:
            currentLayout.columns === 2 && currentLayout.columnHeight === 2,
        });
      }

      sizes.push({
        columns: 4,
        columnHeight: 4,
        icon: "w-3.5 h-3.5",
        dimensions: "4×4",
        isActive:
          currentLayout.columns === 4 && currentLayout.columnHeight === 4,
      });

      if (widget.widget_type.wide) {
        sizes.push({
          columns: 4,
          columnHeight: 2,
          icon: "w-4 h-2.5",
          dimensions: "4×2",
          isActive:
            currentLayout.columns === 4 && currentLayout.columnHeight === 2,
        });
      }

      if (widget.widget_type.tall) {
        sizes.push({
          columns: 2,
          columnHeight: 4,
          icon: "w-2.5 h-4",
          dimensions: "2×4",
          isActive:
            currentLayout.columns === 2 && currentLayout.columnHeight === 4,
        });
      }

      if (widget.widget_type.skinny) {
        sizes.push({
          columns: 4,
          columnHeight: 1,
          icon: "w-4 h-1.5",
          dimensions: "4×1",
          isActive:
            currentLayout.columns === 4 && currentLayout.columnHeight === 1,
        });
      }

      return sizes;
    },
    [previewMode]
  );

  /**
   * Handle mobile drag start - bypass threshold and use proper drag offset
   */
  const handleMobileDragStart = useCallback(
    (e, widgetId) => {
      // Find the actual widget element (not the drag handle)
      const widgetElement = document.querySelector(
        `[data-widget-id="${widgetId}"]`
      );
      if (!widgetElement) return;

      // Prevent default behaviors that can interfere with dragging
      e.preventDefault();
      e.stopPropagation();

      // Create a proper event object that matches what the main drag system expects
      const touch = e.touches?.[0];
      if (!touch) return;

      // Get widget bounds
      const widgetRect = widgetElement.getBoundingClientRect();

      // Calculate proper drag offset to prevent jumping
      // The handle is 28px above the widget and centered horizontally
      // We need to calculate offset as if touch happened at this relative position
      const customDragOffset = {
        x: widgetRect.width / 2, // Center horizontally (handle is centered)
        y: 0, // 28px above widget top (handle is positioned with -top-7 = 28px)
      };

      // Create event with original touch coordinates (no need to fake them now)
      const enhancedEvent = {
        currentTarget: widgetElement,
        target: widgetElement,
        // Original touch properties
        touches: e.touches,
        changedTouches: e.changedTouches,
        // Original coordinates
        clientX: touch.clientX,
        clientY: touch.clientY,
        pageX: touch.pageX,
        pageY: touch.pageY,
        // Event methods
        preventDefault: () => {
          e.preventDefault();
        },
        stopPropagation: () => {
          e.stopPropagation();
        },
        type: "touchstart",
      };

      // Use the main drag system with threshold bypass and custom offset
      dragSystem.handleMouseDown(
        enhancedEvent,
        widgetId,
        true,
        customDragOffset
      );
    },
    [dragSystem]
  );

  /**
   * Render drag handle for mobile
   */
  const renderDragHandle = useCallback(
    (widget) => {
      const isSelected = selectedWidgetId === widget.id;
      const isDragging = dragSystem?.draggedItemId === widget.id;

      // Show drag handle for selected widget (keep it visible during dragging)
      if (!isSelected) {
        return null;
      }

      return (
        <MobileDragHandle
          key={`drag-handle-${widget.id}`}
          widget={widget}
          onDragStart={handleMobileDragStart}
          isLightBackground={isLightBackground}
          isDragging={isDragging}
        />
      );
    },
    [selectedWidgetId, dragSystem, isLightBackground, handleMobileDragStart]
  );

  /**
   * Handle widget delete
   */
  const handleWidgetDelete = useCallback(
    (widgetId) => {
      if (window.confirm("Are you sure you want to delete this widget?")) {
        onWidgetDelete(widgetId);
      }
    },
    [onWidgetDelete]
  );

  /**
   * Theme toggle functionality
   */
  const supportsThemeToggle = useCallback((widget) => {
    return (
      widget.widget_type.type === "rich_text" ||
      widget.widget_type.type === "bio" ||
      widget.widget_type.type === "links"
    );
  }, []);

  const getThemeState = useCallback((widget) => {
    return widget.layout?.settings?.isDarkMode || false;
  }, []);

  const handleThemeToggle = useCallback(
    (widget) => {
      const currentDarkMode = getThemeState(widget);
      const updatedLayout = {
        ...widget.layout,
        settings: {
          ...widget.layout.settings,
          isDarkMode: !currentDarkMode,
        },
      };
      onWidgetUpdate(widget.id, { layout: updatedLayout });
    },
    [onWidgetUpdate, getThemeState]
  );

  const getButtonClass = useCallback((isActive = false, isDisabled = false) => {
    const baseClass =
      "rounded-full p-3 transition-all duration-200 flex items-center justify-center";

    if (isDisabled) {
      return `${baseClass} bg-neutral-800/50 text-white/30 cursor-not-allowed`;
    }

    if (isActive) {
      return `${baseClass} bg-blue-600 text-white hover:bg-blue-700`;
    }

    return `${baseClass} bg-neutral-800 text-white hover:bg-neutral-700`;
  }, []);

  /**
   * Stop propagation helper - clean version without preventDefault issues
   */
  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  /**
   * Render mobile control bar
   */
  const renderMobileControlBar = useCallback(() => {
    const selectedWidget = widgets.find((w) => w.id === selectedWidgetId);
    if (!selectedWidget) return null;

    return (
      <div
        className="fixed bottom-8 left-4 right-4 z-50 flex items-center justify-center"
        onClick={stopPropagation}
      >
        {/* Mobile edit bar with slide-up animation */}
        <div
          className="mobile-edit-bar bg-primary border border-primary rounded-full shadow-lg px-6 py-2 h-[56px] flex items-center justify-between w-full relative animate-slide-up"
          onClick={stopPropagation}
          style={{
            touchAction: "manipulation", // Optimize for button taps
          }}
        >
          {/* Left side: Action buttons */}
          <div className="flex items-center space-x-3">
            {/* Delete button - moved to the left */}
            <button
              onClick={(e) => {
                stopPropagation(e);
                handleWidgetDelete(selectedWidget.id);
              }}
              className="rounded-full p-3 transition-all duration-200 flex items-center justify-center space-x-2 text-white hover:bg-red-600/30 bg-red-600/20"
            >
              <FontAwesomeIcon icon={faTrash} size="1x" />
            </button>

            {/* Config button - always show, grey out if not available */}
            <button
              onClick={(e) => {
                stopPropagation(e);
                if (selectedWidget.widget_type?.config_enabled !== false) {
                  handleMobileSettingsOpen(selectedWidget.id, "config");
                }
              }}
              className={getButtonClass(
                false,
                selectedWidget.widget_type?.config_enabled === false
              )}
              disabled={selectedWidget.widget_type?.config_enabled === false}
              title={
                selectedWidget.widget_type?.config_enabled === false
                  ? "Configuration not available for this widget"
                  : "Configure widget"
              }
            >
              <FontAwesomeIcon icon={faCog} size="1x" />
            </button>

            {/* Layout/Theme button - always show */}
            <button
              onClick={(e) => {
                stopPropagation(e);
                handleMobileSettingsOpen(selectedWidget.id, "layout");
              }}
              className={getButtonClass()}
              title="Layout & Theme settings"
            >
              <FontAwesomeIcon icon={faPaintBrush} size="1x" />
            </button>

            {/* Size selector - now round like other buttons */}
            <button
              onClick={(e) => {
                stopPropagation(e);
                setShowSizeMenu(!showSizeMenu);
              }}
              className={getButtonClass()}
              title="Change widget size"
            >
              <div
                className={`border-2 border-current rounded ${getCurrentSizeIcon(selectedWidget)}`}
              />
            </button>

            {/* Day/Night Mode Toggle - only show if widget supports it */}
            {supportsThemeToggle(selectedWidget) && (
              <button
                onClick={(e) => {
                  stopPropagation(e);
                  handleThemeToggle(selectedWidget);
                }}
                className={getButtonClass()}
                title={
                  getThemeState(selectedWidget)
                    ? "Switch to light mode"
                    : "Switch to dark mode"
                }
              >
                <FontAwesomeIcon
                  icon={getThemeState(selectedWidget) ? faSun : faMoon}
                  size="1x"
                />
              </button>
            )}
          </div>

          {/* Done button */}
          <button
            onClick={(e) => {
              stopPropagation(e);
              onWidgetDeselect();
            }}
            className="bg-black text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-black/90 transition-colors"
          >
            Done
          </button>
        </div>

        {/* Size selection menu */}
        {showSizeMenu && (
          <div
            className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-4 bg-primary border border-primary rounded-2xl shadow-lg p-3"
            onClick={stopPropagation}
          >
            <div
              className="grid gap-2"
              style={{
                gridTemplateColumns: `repeat(${Math.min(getAvailableSizes(selectedWidget).length, 4)}, 1fr)`,
              }}
            >
              {getAvailableSizes(selectedWidget).map((size) => (
                <button
                  key={`${size.columns}x${size.columnHeight}`}
                  onClick={(e) => {
                    stopPropagation(e);
                    handleWidgetResize(
                      selectedWidget.id,
                      size.columns,
                      size.columnHeight
                    );
                  }}
                  className={`flex flex-col items-center justify-center p-2 rounded-lg transition-all ${
                    size.isActive
                      ? "bg-primary text-white"
                      : "bg-neutral-800 text-white/70 hover:bg-neutral-700 hover:text-white"
                  }`}
                >
                  <div
                    className={`border-2 border-current rounded ${size.icon} mb-1`}
                  />
                  <span className="text-xs text-white/60">
                    {size.dimensions}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }, [
    selectedWidgetId,
    widgets,
    previewMode,
    onWidgetDeselect,
    handleMobileSettingsOpen,
    handleWidgetResize,
    handleWidgetDelete,
    handleThemeToggle,
    supportsThemeToggle,
    getThemeState,
    showSizeMenu,
    getCurrentSizeIcon,
    getAvailableSizes,
    getButtonClass,
    stopPropagation,
  ]);

  /**
   * Render widget selection overlay
   */
  const renderWidgetSelectionOverlay = useCallback(
    (widget) => {
      const isSelected = selectedWidgetId === widget.id;
      const isDragging = dragSystem?.draggedItemId === widget.id;
      const shouldBlur = selectedWidgetId && !isSelected && !isDragging;

      return (
        <div
          key={`overlay-${widget.id}`}
          className={`absolute inset-0 z-10 pointer-events-auto transition-all duration-300 rounded-3xl ${
            shouldBlur ? "backdrop-blur-[2px] bg-black/5" : ""
          }`}
          onClick={(e) => {
            // Only handle tap if not dragging, not touching mobile edit bar, and not touching drag handle
            if (
              !dragSystem?.isDragging &&
              !e.target.closest(".mobile-edit-bar") &&
              !e.target.closest(".mobile-drag-handle")
            ) {
              handleWidgetTap(widget.id, e);
            }
          }}
          style={{
            touchAction: "manipulation",
          }}
        >
          {/* Drag handle */}
          {renderDragHandle(widget)}

          {/* Selected indicator removed - no more blue checkmark */}
        </div>
      );
    },
    [
      selectedWidgetId,
      dragSystem?.draggedItemId,
      dragSystem?.isDragging,
      handleWidgetTap,
      renderDragHandle,
    ]
  );

  /**
   * Render mobile settings drawer
   */
  const renderMobileSettingsDrawer = useCallback(() => {
    return (
      <MobileSettingsDrawer
        isOpen={mobileDrawer.isOpen}
        onClose={handleMobileDrawerClose}
        widget={mobileDrawer.widget}
        settingsType={mobileDrawer.settingsType}
        onLayoutChange={handleMobileLayoutChange}
        onConfigChange={handleMobileConfigChange}
        previewMode={previewMode}
        allWidgets={widgets}
      />
    );
  }, [
    mobileDrawer,
    handleMobileDrawerClose,
    handleMobileLayoutChange,
    handleMobileConfigChange,
    previewMode,
    widgets,
  ]);

  return {
    renderWidgetSelectionOverlay,
    renderMobileControlBar,
    renderMobileSettingsDrawer,
    isDragging: dragSystem?.isDragging,
    draggedItemId: dragSystem?.draggedItemId,
    dragPosition: dragSystem?.dragPosition,
    potentialDropArea: dragSystem?.potentialDropArea,
    isDropAnimating: dragSystem?.isDropAnimating,
    selectedWidgetId,
    handleContainerTap,
  };
}
